/**
 * @author PoornaChandu
 */
import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import Highlighter from 'react-highlight-words';
import { useNavigate } from 'react-router-dom';
import { message, Popconfirm, Switch, Input, Button, Tabs, Tag, Spin } from 'antd';
import { DeleteOutlined, FormOutlined, SearchOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import constants from './constants';
import translation from '../../utils/translation';
import localStorage from '../../utils/localStorage';
import StyledTable from "../../commons/styles/table";
import { RuleButton } from './style';
import { StyledHomepage } from '../../commons/styles/layout';
import {
  ruleList,
  deleteRule,
  updateRuleStatus,
  getRuleCounts,
  updateNotificationStatus,
  getRules,
  aiDeleteRule
} from './Apicalls';

const RulesList = (props) => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [AIList, setAIList] = useState([]);
  const [isAI, setIsAI] = useState(false);
  const [countsData, setCountsData] = useState({});
  const [userProfile, setUserProfile] = useState(props.userProfile || {});
  const [activeKey, setActiveKey] = useState('1');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const searchInput = useRef(null);

  const siteId = localStorage.get('currentSite');
  const permissionsArray = useMemo(() => {
    if (
      userProfile &&
      userProfile.belongsTo &&
      userProfile.belongsTo.sites &&
      userProfile.belongsTo.sites[siteId]
    ) {
      const role = userProfile.belongsTo.sites[siteId].role;
      if (role && role.Permission) {
        return role.Permission
          .filter((item) => item.resource === 'rule_engine')
          .map((name) => name.action);
      }
    }
    return [];
  }, [userProfile, siteId]);

  useEffect(() => {
    setIsAI(permissionsArray.includes('ai'));
  }, [permissionsArray]);

  // Fetch data with loading and error handling
  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await ruleList();
      const AIListData = await getRules();
      setTableData(data);
      setAIList(AIListData);
      await countsReferesh();
    } catch (err) {
      message.warn('OOPS Something went Wrong');
    } finally {
      setLoading(false);
    }
  };

  const countsReferesh = async () => {
    try {
      const countsData = await getRuleCounts();
      setCountsData(countsData);
    } catch (err) {
      // handle error silently
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (props.userProfile !== userProfile) {
      setUserProfile(props.userProfile);
    }
  }, [props.userProfile]);

  // Debounced search to improve performance
  const debouncedSetSearchText = useMemo(
    () => debounce((text) => setSearchText(text), 300),
    []
  );

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
    debouncedSetSearchText(selectedKeys[0]);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    handleSearch([], confirm);
  };

  const handleDelete = async (key) => {
    setLoading(true);
    try {
      const data = await aiDeleteRule(key);
      if (data.status === 200) {
        message.success('Deleted Rule');
        const AIListData = await getRules();
        setAIList(AIListData);
      }
    } catch (err) {
      message.warn('Something went Wrong !');
    } finally {
      setLoading(false);
    }
  };

  const DeleteRule = async (key) => {
    setLoading(true);
    try {
      const data = await deleteRule(key);
      if (data.status === 200) {
        message.success('Rule Deleted');
        await fetchData();
      }
    } catch (err) {
      message.warn('Something went Wrong !');
    } finally {
      setLoading(false);
    }
  };

  const ruleStatusUpdate = async (rule) => {
    setLoading(true);
    const payLoad = {
      Id: rule?.Id,
      Type: rule?.Type,
      RuleStatus: !rule.RuleStatus,
    };
    try {
      const data = await updateRuleStatus(payLoad);
      if (data && data.status === 200) {
        await fetchData();
      } else {
        message.error('Something Went Wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const notificationStatusUpdate = async (rule) => {
    setLoading(true);
    const payLoad = {
      Id: rule?.Id,
      Type: rule?.Type,
      NotificationStatus: !rule.NotificationStatus,
      FilterOption: rule.Type === 'batch' ? rule.Priority : rule.ParameterType,
    };
    try {
      const data = await updateNotificationStatus(payLoad);
      if (data.status === 200) {
        await fetchData();
      } else {
        message.error('Something Went Wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (ruleId) => {
    navigate(`/rubus/RulesFeedback/?ruleId=${ruleId}`);
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
          aria-label={`Search ${dataIndex}`}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon={<SearchOutlined />}
          style={{ width: 90, marginRight: 8 }}
        >
          {translation(props.translationCache, constants.search)}
        </Button>
        <Button
          onClick={() => handleReset(clearFilters, confirm)}
          style={{ width: 90 }}
        >
          {translation(props.translationCache, constants.Reset)}
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record?.[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) => (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text?.toString()}
      />
    )
  });

  const columns = [
    {
      title: translation(props.translationCache, constants.Rule_Name),
      dataIndex: 'DisplayRuleName',
      key: 'DisplayRuleName',
      ...getColumnSearchProps('DisplayRuleName', translation(props.translationCache, constants.Rule_Name))
    },
    {
      title: translation(props.translationCache, constants.Rule_Description),
      dataIndex: 'Description',
      key: 'Description',
      ...getColumnSearchProps('Description', translation(props.translationCache, constants.Rule_Description))
    },
    {
      title: translation(props.translationCache, constants.createdTime),
      dataIndex: 'CreatedDate',
      key: 'CreatedDate',
      ...getColumnSearchProps('CreatedDate', translation(props.translationCache, constants.createdTime)),
      render: (text) => <span>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</span>
    },
    {
      title: translation(props.translationCache, constants.edit),
      key: 'Edit',
      render: (text, record) => (
        <span>
          <FormOutlined
            aria-label="Edit Rule"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/rubus/RuleEngine/${record.Id}`);
            }}
          />
        </span>
      )
    },
    {
      title: translation(props.translationCache, constants.delete),
      key: 'Delete',
      render: (text, record) => (
        <span>
          <Popconfirm
            placement="topRight"
            title="Are you sure delete this Rule?"
            onConfirm={(e) => {
              e.stopPropagation();
              DeleteRule(record.Id);
            }}
            onCancel={(e) => {
              e.stopPropagation();
            }}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined
              style={{ color: '#f5222d' }}
              aria-label="Delete Rule"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </Popconfirm>
        </span>
      )
    },
    {
      title: translation(props.translationCache, constants.RuleStatus),
      key: 'RuleStatus',
      render: (text, record) => (
        <span>
          <Switch
            checkedChildren={
              props.translationCache?.['Active'] || 'Active'
            }
            unCheckedChildren={
              props.translationCache?.['InActive'] || 'InActive'
            }
            checked={record.RuleStatus}
            onChange={() => ruleStatusUpdate(record)}
            style={{
              backgroundColor: record.RuleStatus ? '#416a41' : '#9f3f47',
            }}
            aria-label="Toggle Rule Status"
          />
        </span>
      )
    },
    {
      title: translation(props.translationCache, constants.status),
      key: 'NotificationStatus',
      render: (text, record) => (
        <span>
          <Switch
            checkedChildren={
              props.translationCache?.['Active'] || 'Active'
            }
            unCheckedChildren={
              props.translationCache?.['InActive'] || 'InActive'
            }
            checked={record.NotificationStatus}
            onChange={() => notificationStatusUpdate(record)}
            style={{
              backgroundColor: record.RuleStatus ? '#416a41' : '#9f3f47',
            }}
            aria-label="Toggle Notification Status"
          />
        </span>
      )
    },
    {
      title: 'Rule History',
      key: 'RuleHistory',
      render: (text, record) => (
        <RuleButton
          type="primary"
          onClick={() => handleRowClick(record.Id)}
        >
          {translation(props.translationCache, constants.RuleHistory)}
        </RuleButton>
      )
    },
    {
      title: 'Source',
      dataIndex: 'AIRule',
      key: 'AIRule',
      ...getColumnSearchProps('AIRule', 'Source'),
      render: (text, record) => (
        record.AIRule ? (
          <Tag color="geekblue">AI RULE</Tag>
        ) : (
          <Tag color="green">MANUAL RULE</Tag>
        )
      )
    },
    {
      title: translation(props.translationCache, 'Redirect'),
      render: (text, record) => {
        if (record.AIRule && isAI) {
          return (
            <Button
              style={{ color: '#1a3652' }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/rubus/AIRulesDetails?Id=${record.RuleGenHeaderId}`);
              }}
            >
              {translation(props.translationCache, 'Redirect to PS')}
            </Button>
          );
        }
        return null;
      }
    }
  ];

  const AIcolumns = [
    {
      title: translation(props.translationCache, constants.ProblemStatement),
      dataIndex: 'ProblemStatement',
      key: 'ProblemStatement',
      ...getColumnSearchProps('ProblemStatement', translation(props.translationCache, constants.ProblemStatement))
    },
    {
      title: translation(props.translationCache, constants.CurrentStep),
      dataIndex: 'CurrentStep',
      key: 'CurrentStep',
      ...getColumnSearchProps('CurrentStep', translation(props.translationCache, constants.CurrentStep))
    },
    {
      title: translation(props.translationCache, 'Status'),
      dataIndex: 'RuleStatus',
      key: 'RuleStatus',
      ...getColumnSearchProps('RuleStatus', translation(props.translationCache, 'Status'))
    },
    {
      title: translation(props.translationCache, constants.createdTime),
      dataIndex: 'CreatedDate',
      key: 'CreatedDate',
      ...getColumnSearchProps('CreatedDate', translation(props.translationCache, constants.createdTime)),
      render: (text) => <span>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</span>
    },
    {
      title: translation(props.translationCache, constants.edit),
      key: 'Edit',
      render: (text, record) => (
        <span>
          <FormOutlined
            aria-label="Edit AI Rule"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/rubus/AIRulesDetails?Id=${record.Id}`);
            }}
          />
        </span>
      )
    },
    {
      title: translation(props.translationCache, constants.delete),
      key: 'Delete',
      render: (text, record) => (
        <span>
          {record.RuleStatus !== 'Completed' ? (
            <Popconfirm
              title="Are you sure delete this AI Rule?"
              onConfirm={(e) => {
                e.stopPropagation();
                handleDelete(record.Id);
              }}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined
                style={{ color: 'red' }}
                aria-label="Delete AI Rule"
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          ) : null}
        </span>
      )
    }
  ];

  const handleTabChange = (key) => setActiveKey(key);

  const handleViewMaintanence = (e) => {
    e.stopPropagation();
    navigate(`/rubus/Rule/Maintainance`);
  };

  const TabStyles = {
    default: { color: 'white' },
    active: { color: '#1890ff' }
  };

  return (
    <StyledHomepage>
      <Tabs defaultActiveKey="1" onChange={handleTabChange} activeKey={activeKey}>
        <Tabs.TabPane
          tab={
            <span style={activeKey === '1' ? TabStyles.active : TabStyles.default}>
              {translation(props.translationCache, 'Rules')}
            </span>
          }
          key="1"
        >
          <Fragment>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row-reverse',
                color: '#fff !important',
                marginTop: '10px'
              }}
            >
              <Button
                type="primary"
                disabled={!permissionsArray.includes('create')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/rubus/RuleEngine`);
                }}
              >
                {translation(props.translationCache, constants.addrule) || `${constants.addrule}`}
              </Button>
              <Button
                type="primary"
                style={{ marginRight: '10px' }}
                onClick={handleViewMaintanence}
              >
                {translation(props.translationCache, constants.Rule_Maintainence) || `${constants.Rule_Maintainence}`}
              </Button>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row-reverse',
                color: 'white',
                margin: '10px 0px'
              }}
            >
              {countsData && !Array.isArray(countsData) ? (
                <span>
                  <span>
                    {translation(props.translationCache, 'Total Count')} : {countsData.TotalCount || '-'} ,{' '}
                  </span>
                  <span>
                    {translation(props.translationCache, 'Active Count')} : {countsData.ActiveCount || '-'} ,{' '}
                  </span>
                  <span>
                    {translation(props.translationCache, 'Inactive Count')} : {countsData.InActiveCount || '-'}{' '}
                  </span>
                </span>
              ) : null}
            </div>
            <Spin spinning={loading}>
              <StyledTable
                size="small"
                columns={columns}
                dataSource={tableData}
                rowKey="Id"
                pagination={{ pageSize: 10 }}
              />
            </Spin>
          </Fragment>
        </Tabs.TabPane>
        {isAI && (
          <Tabs.TabPane
            tab={
              <span style={activeKey === '2' ? TabStyles.active : TabStyles.default}>
                {translation(props.translationCache, 'AI Rules')}
              </span>
            }
            key="2"
          >
            <Spin spinning={loading}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  color: '#fff !important',
                  marginTop: '10px'
                }}
              >
                <Button
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/rubus/AIRulesDetails`);
                  }}
                >
                  {translation(props.translationCache, 'Create AI Rule')}
                </Button>
                <Button
                  type="primary"
                  style={{ marginRight: '5px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/rubus/AI/fileupload`);
                  }}
                >
                  {translation(props.translationCache, 'Redirect to Upload page')}
                </Button>
              </div>
              <div style={{ marginTop: '25px' }}>
                <StyledTable
                  size="small"
                  columns={AIcolumns}
                  dataSource={AIList}
                  rowKey="Id"
                  pagination={{ pageSize: 10 }}
                />
              </div>
            </Spin>
          </Tabs.TabPane>
        )}
      </Tabs>
    </StyledHomepage>
  );
};

export default connect((state) => ({
  userProfile: state.LayoutReducer.loggedInProfile,
  translationCache: state.TranslationReducer.translationCache,
}))(RulesList);
